import { FC, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Label,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@capacit-com/lara-law-shared-ui";
import { Undo2, X } from "lucide-react";
import isEqual from "lodash/isEqual";
import type { WidgetInstance } from "@/models/dashboard/Dashboard";
import useDashboardStore from "@/stores/dashboard.store";
import WidgetConfigFields from "../config/WidgetConfigFields";
import { getWidgetTypeInfo } from "../lib/widgetCatalog";

interface WidgetEditorProps {
  widget: WidgetInstance;
  /** Just placed from the gallery: its default name is selected so typing replaces it. */
  isNew?: boolean;
  onClose: () => void;
  onDraftStateChange: (hasDraft: boolean) => void;
}

/**
 * The editor view of the panel (LAR-726). It keeps no copy of the widget:
 * every change patches the dashboard draft, and the widget on the grid — which
 * renders from that draft — is the preview.
 */
const WidgetEditor: FC<WidgetEditorProps> = ({
  widget,
  isNew = false,
  onClose,
  onDraftStateChange,
}) => {
  const updateWidget = useDashboardStore((s) => s.updateWidget);
  const restoreWidgetSnapshot = useDashboardStore(
    (s) => s.restoreWidgetSnapshot
  );
  const snapshot = useDashboardStore((s) => s.editingWidgetSnapshot);
  // The fields keep a little state of their own (an uncontrolled search box, a
  // half-typed shortcut); remounting them is how a restore reaches it.
  const [fieldsRevision, setFieldsRevision] = useState(0);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Read once: the name is selected when the editor opens, not when isNew later flips.
  const selectNameOnOpenRef = useRef(isNew);

  // Focus moves into the panel when it opens or switches widget.
  useEffect(() => {
    nameInputRef.current?.focus();
    if (selectNameOnOpenRef.current) nameInputRef.current?.select();
  }, []);

  const info = getWidgetTypeInfo(widget.type);
  const hasChanges =
    snapshot !== null &&
    (snapshot.name !== widget.name || !isEqual(snapshot.config, widget.config));

  const handleRestore = () => {
    restoreWidgetSnapshot();
    onDraftStateChange(false);
    setFieldsRevision((revision) => revision + 1);
    // The button disables itself; keep the caret in the panel.
    nameInputRef.current?.focus();
  };

  const fields = (
    <WidgetConfigFields
      key={fieldsRevision}
      type={widget.type}
      config={widget.config}
      onChange={(config) => updateWidget(widget.id, { config })}
      onDraftStateChange={onDraftStateChange}
    />
  );

  return (
    <>
      <SheetHeader className="flex-row items-start justify-between gap-3 border-b p-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
            <info.icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-1">
            <SheetTitle>Rediger widget</SheetTitle>
            <SheetDescription>
              {info.label} · ændringer vises med det samme på dashboardet
            </SheetDescription>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          aria-label="Luk panelet"
        >
          <X className="h-4 w-4" />
        </Button>
      </SheetHeader>

      <div className="space-y-6 p-4 md:min-h-0 md:flex-1 md:overflow-y-auto">
        <div className="space-y-2">
          <Label htmlFor="widget-editor-name">Navn</Label>
          <Input
            ref={nameInputRef}
            id="widget-editor-name"
            value={widget.name}
            onChange={(e) => updateWidget(widget.id, { name: e.target.value })}
            aria-invalid={!widget.name.trim()}
            aria-describedby={
              !widget.name.trim() ? "widget-editor-name-error" : undefined
            }
          />
          {!widget.name.trim() && (
            <p
              id="widget-editor-name-error"
              className="text-xs text-destructive"
            >
              Giv widgetten et navn.
            </p>
          )}
        </div>

        {widget.type === "notifications" ? (
          <p className="text-sm text-muted-foreground">
            Notifikationer har ingen indstillinger.
          </p>
        ) : (
          fields
        )}
      </div>

      <SheetFooter className="flex-row flex-wrap items-center justify-between gap-2 border-t p-4">
        <Button
          variant="ghost"
          onClick={handleRestore}
          disabled={!hasChanges}
          id="widget-editor-restore"
        >
          <Undo2 className="mr-2 h-4 w-4" />
          Fortryd ændringer i widgetten
        </Button>
        <Button onClick={onClose} id="widget-editor-done">
          Færdig
        </Button>
      </SheetFooter>
    </>
  );
};

export default WidgetEditor;
